import { setAddMsgInCalendarContact } from "@common-reducers/WhatsAppReducer";
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { isExtensionContext } from "@common-services/detect-context";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { ContactPickerField, SelectedContact } from "@extension/context/content/components/shared";
// Canonical contact-picker shape (#40 consolidated). Aliased to
// `MessageContact` so the existing call sites read naturally without
// renames; the type lives in schedule-message-types.ts.
import type { ContactPickItem as MessageContact } from "../schedule-message-types";
import { namespace } from '@common-config/constants';

interface ContactSelectProps {
    onContactSelect?: (contact: MessageContact) => void;
    onContactRemove?: (contactId: string) => void;
    selectedContactIds?: string[];
    selectedContacts?: MessageContact[];
}

export const ContactSelect: React.FC<ContactSelectProps> = ({
    onContactSelect,
    onContactRemove,
    selectedContactIds = [],
    selectedContacts = [],
}) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(namespace);

    const seedSelectedContacts = useMemo<SelectedContact[]>(
        () => selectedContacts.map(c => ({
            id: c.id,
            name: c.name,
            displayName: c.displayName,
            imgUrl: c.imgUrl,
        })),
        [selectedContacts],
    );

    const handleSelected = useCallback((picked: SelectedContact) => {
        const newContact: MessageContact = {
            id: picked.id,
            name: picked.name || picked.id,
            displayName: picked.displayName || picked.name || picked.id,
            imgUrl: picked.imgUrl,
        };
        if (onContactSelect) {
            onContactSelect(newContact);
        } else {
            dispatch(setAddMsgInCalendarContact(newContact));
        }
    }, [onContactSelect, dispatch]);

    return (
        <ContactPickerField
            collapsible
            value={selectedContactIds}
            selectedContacts={seedSelectedContacts}
            onContactSelected={handleSelected}
            onContactRemoved={onContactRemove}
            showRefreshButton={!isExtensionContext()}
            height={320}
            // No "* scheduled individually" footnote: the summary line already
            // states the fan-out as a consequence ("3 messages — one to each
            // recipient") once there is more than one, and an always-on
            // asterisk line was noise on every open.
            searchPlaceholder={selectedContactIds.length > 0
                ? t('contacts.addMore')
                : t('contacts.searchPlaceholder')}
        />
    );
};
